import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('auth_user');
      }
    }
    setLoading(false);
  }, []);

  const signInWithGoogle = async () => {
    // Use Stack Auth SDK's built-in OAuth with React 19
    const { stackClient } = await import('../lib/stack');
    
    try {
      await stackClient.signInWithOAuth('google');
      // Stack Auth will handle the redirect and callback
    } catch (error) {
      console.error('Stack Auth OAuth error:', error);
      alert('Sign in failed. Please try again or contact support.');
    }
  };

  const signUpWithEmail = async (email, firstName, lastName, sport) => {
    // Create user profile
    const newUser = {
      id: 'user_' + Date.now(),
      email,
      displayName: `${firstName} ${lastName}`,
      firstName,
      lastName,
      sport,
      provider: 'email',
      role: 'user', // Standard user role
      createdAt: new Date().toISOString()
    };
    
    // Store user in auth
    setUser(newUser);
    localStorage.setItem('auth_user', JSON.stringify(newUser));
    
    // Also store in users database
    const users = JSON.parse(localStorage.getItem('users_db') || '{}');
    users[email] = newUser;
    localStorage.setItem('users_db', JSON.stringify(users));
    
    return newUser;
  };

  const signOut = async () => {
    console.log('🚪 Signing out...');
    
    // Clear local state first
    setUser(null);
    localStorage.removeItem('auth_user');
    
    // Clear all Stack Auth cookies
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.includes('stack') || cookieName.includes('__Host-stack')) {
        // Delete cookie for all possible paths and domains
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
        console.log(`🍪 Cleared cookie: ${cookieName}`);
      }
    }
    
    // Sign out from Stack Auth (non-blocking, don't wait for redirect)
    try {
      const { stackClient } = await import('../lib/stack');
      // Call signOut but don't await the redirect
      stackClient.signOut().catch(err => console.log('Stack signOut redirect prevented'));
    } catch (error) {
      console.error('Stack Auth sign out error:', error);
    }
    
    console.log('✅ Sign out complete');
  };

  const isAdmin = user?.email === 'rao.vinu@gmail.com';

  const value = {
    user,
    setUser,
    loading,
    signInWithGoogle,
    signUpWithEmail,
    signOut,
    isAdmin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
